import uniqBy from 'lodash/uniqBy';
import orderBy from 'lodash/orderBy';

import * as React from 'react';
import { ItemType } from '../../classes/item-type';
import { PriceType } from '../../classes/price-type';
import { CartSeat, Seat, SeatPricing, SeatZone } from '../../classes/seat';
import { getCurrencyFormat } from '../../helpers/currency-format';
import { Html } from '../../helpers/html-helpers';
import { sortPriceTypes } from '../../helpers/price-type-helpers';
import { isNullOrEmpty } from '../../helpers/string-format';
import { IAppSettingsModel } from '../../reducers/fetch-settings-reducer';


interface ISeatHoverProps {
    xPosition: number;
    yPosition: number;
    seats: Array<Seat>;
    cartSeats: Array<CartSeat>;
    levelPricing: Array<SeatZone>;
    selectedPriceTypes: Array<PriceType>;
    appSettingsModel: IAppSettingsModel;
    mouseOutHover: () => void;
    ariaLabelId: string;
    showHover: boolean;
    dispatch: any;
    isChrome: boolean;
}

interface ISeatHoverState {
}

class SeatHover extends React.Component<ISeatHoverProps, ISeatHoverState>{
    constructor(props) {
        super(props);
    }

    shouldComponentUpdate(nextProps: ISeatHoverProps) {
        return this.props.xPosition != nextProps.xPosition
            || this.props.yPosition != nextProps.yPosition
            || this.props.showHover != nextProps.showHover;
    }

    hoverBoxClassName = 'seat-tooltip';

    getSelectedPrices(props: ISeatHoverProps): Array<SeatPricing> {
        // If no seats, no prices
        if (props.seats.length === 0 || props.seats.every(s => !s.tessituraSeat.isAvailable)) {
            return [];
        }

        // Get the prices for this zone
        const zonePrices = props.levelPricing.filter(pricing => pricing.zoneId === props.seats[0].tessituraSeat.zoneId);
        if (zonePrices.length < 1) {
            return [];
        }

        var sortedPriceTypes = sortPriceTypes(props.selectedPriceTypes);
        // Get the id's of the selected price types
        const selectedPriceTypeIds = sortedPriceTypes.map(pt => pt.priceTypeId);

        if (selectedPriceTypeIds.length === 0) {
            return zonePrices[0].prices;
        }

        // Get the unique prices from the zone that are also selected
        return orderBy(uniqBy(zonePrices[0].prices
            .filter(price => selectedPriceTypeIds.indexOf(price.priceTypeId) >= 0),
            price => price.priceTypeId),
            pr => selectedPriceTypeIds.indexOf(pr.priceTypeId)
        );
    }

    getSeatsRemainingText(): string {
        const seatsRemaining = this.props.seats.filter(seat => seat.tessituraSeat.isAvailable).length;

        return this.fetchHoverText(seatsRemaining, this.props.appSettingsModel.seatsRemainingText);
    }

    fetchHoverText(response, seatsAvailableText) {
        return seatsAvailableText.replace("{0}", response.toString());
    }

    renderFeeAmount(price: SeatPricing) {
        return price?.feeAmount > 0 && this.props.appSettingsModel?.displaySeatFee && <div className="small-italic">
            <p>{this.props.appSettingsModel?.priceFeeHeading}</p>
            <p>{this.props.appSettingsModel?.ticketCostText} {getCurrencyFormat(this.props.appSettingsModel.currencyFormat, price.price)}</p>
            <p>{this.props.appSettingsModel?.feeAmountText} {getCurrencyFormat(this.props.appSettingsModel.currencyFormat, price.feeAmount)}</p>
        </div>;
    }

    render() {
        var selectedPrices = this.getSelectedPrices(this.props);
        let section = this.props.seats.length > 0 ? this.props.appSettingsModel.useZoneDescription && this.props.seats[0].zoneDescription !== null ? this.props.seats[0].zoneDescription : this.props.seats[0].tessituraSeat.sectionDescription : '';

        const hasSeatHoverMessage = !isNullOrEmpty(this.props.appSettingsModel.seatHoverMessageText);
        const hasSeatingUnlockMessage = this.props.seats[0].tessituraSeat.isDisabled && this.props.seats[0].seatingOptionGroup !== null && this.props.seats[0].seatingOptionGroup.unlockSeatingOptionGroupId != null;
        const hasSmallPromptMessage = !isNullOrEmpty(this.props.seats[0].seatStyleCulture.smallPrompt);

        let priceToDisplay = "";
        if (selectedPrices.length > 0 && !this.props.appSettingsModel.hidePricesOnSeatHover) {
            const showFreeExchangePrice = this.props.appSettingsModel.itemType === ItemType.Exchange && selectedPrices[0].price === 0
                && this.props.appSettingsModel.exchangeFreeLabel.length > 0;

            if (showFreeExchangePrice) {
                priceToDisplay = this.props.appSettingsModel.exchangeFreeLabel;
            } else if (selectedPrices.length > 0) {
                priceToDisplay = getCurrencyFormat(this.props.appSettingsModel.currencyFormat, selectedPrices[0].price + selectedPrices[0].feeAmount);
            }
        }

        return (
            <foreignObject style={{ position: 'absolute' }}
                x={0}
                y={0}
                width="260" height="220" id="seat-tooltip-fo"
                onMouseOut={(e) => {
                    this.setState({ ...this.state, hovered: false });
                    var hoverRect = document.getElementsByClassName('hover-map-svg')[0].getBoundingClientRect()
                    var inX = hoverRect.left <= e.clientX && e.clientX <= hoverRect.right;
                    var inY = hoverRect.top <= e.clientY && e.clientY <= hoverRect.bottom;

                    this.props.mouseOutHover();

                }}>
                <div
                    className={this.hoverBoxClassName
                        + (this.props.isChrome ? ' tooltip-chrome' : '')
                        + (this.props.yPosition <= (window.innerHeight / 2) ? ' tooltip-up' : ' tooltip-down')
                        + (this.props.showHover ? ' tooltip-show' : ' tooltip-hide')} id={this.props.ariaLabelId}>
                    <div className="seat-tooltip-header">
                        <div className="seat-tooltip-header-section">
                            {
                                this.props.seats.length > 0 ?
                                    <span>{this.props.seats.length > 1 ? this.props.appSettingsModel.groupSeatCartItemText.replace("{0}", "").replace("{1}", section)
                                        : `${section}`}
                                    </span>
                                    : null
                            }
                        </div>
                        <div className="seat-tooltip-header-seat">
                            <span>{this.props.seats.length > 1 ? this.props.appSettingsModel.groupSeatCartItemText.replace("{0}", "").replace("{1}", section)
                                : `${this.props.seats[0].tessituraSeat.rowText}-${this.props.seats[0].tessituraSeat.numberText}`}
                            </span>

                        </div>
                    </div>

                    {
                        selectedPrices.length > 0 ?
                            <div className="seat-tooltip-prices">
                                <div className="seat-tooltip-prices-list">
                                    {selectedPrices.length === 1 ?
                                        <>
                                            <span className="seat-tooltip-prices-list-text">
                                                {priceToDisplay.length > 0 ? priceToDisplay + " - " : ""}{selectedPrices[0].priceTypeDescription}
                                            </span>
                                            {this.props.appSettingsModel?.displaySeatFee && this.renderFeeAmount(selectedPrices[0])}
                                        </>
                                        :
                                        orderBy(selectedPrices, selectedPrice => selectedPrice.price).map((price: SeatPricing) => {
                                            return <div key={price.priceTypeId} className="seat-tooltip-prices-list-item">
                                                <span className="seat-tooltip-prices-list-text">{getCurrencyFormat(this.props.appSettingsModel.currencyFormat, price.price + price.feeAmount)} - {price.priceTypeDescription}</span>
                                                {this.renderFeeAmount(price)}
                                            </div>
                                        })
                                    }
                                </div>
                            </div>
                            :
                            null
                    }

                    {
                        this.props.seats.length > 0 && (hasSeatHoverMessage || hasSeatingUnlockMessage || hasSmallPromptMessage) ?
                            <div>
                                {
                                    hasSeatHoverMessage ?
                                        <div className="seat-tooltip-options">
                                            {Html.renderDiv(this.props.appSettingsModel.seatHoverMessageText, "seat-info-options-copy")}
                                        </div>
                                        : null
                                }
                                {
                                    hasSeatingUnlockMessage ?
                                        <div className="seat-tooltip-options">
                                            <div className="seat-tooltip-options-copy">
                                                {this.props.seats[0].seatingOptionGroup.addSeatsToUnlockMessage}
                                            </div>
                                        </div>
                                        :
                                        hasSmallPromptMessage ?
                                            <div className="seat-tooltip-options">
                                                {Html.renderDiv(this.props.seats[0].seatStyleCulture.smallPrompt, "seat-info-options-copy")}
                                            </div>
                                            : null
                                }
                            </div>
                            :
                            null
                    }
                    {
                        (this.props.seats.length > 1) && Html.renderDiv(this.getSeatsRemainingText(), 'seat-tooltip-remaining')
                    }

                    {
                        ((this.props.seats.length > 0) && (this.props.seats[0].tessituraSeat.isAvailable) && selectedPrices.length == 0) && Html.renderDiv(this.props.appSettingsModel.seatHoverMissingPriceTypesMessage, 'seat-tooltip-remaining')
                    }

                </div>
            </foreignObject >
        );
    }

    componentDidMount() {
        var hoverBox = document.getElementsByClassName(this.hoverBoxClassName)[0];
        var height = hoverBox.getBoundingClientRect().height;
        var hoverFO = document.getElementsByTagName('foreignObject')[0];
        hoverFO.setAttribute('height', height.toString());

        var hoverViewBox = document.getElementsByClassName('hover-map-svg')[0];
        hoverViewBox.setAttribute('viewBox', `0 0 260 ${height}`);

    }

}

export default SeatHover;